import { useSelector } from "react-redux";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { walletService } from "../services/walletService";
import { CreditCardIcon, PlusIcon, TrendingUpIcon, TrendingDownIcon, CheckCircleIcon, CalendarIcon, GiftIcon } from "../assets/icons";

const useQuery = () => {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
};

const WalletPage = () => {
  const { token, onboarding } = useSelector((state) => state.user);
  const navigate = useNavigate();
  const query = useQuery();
  const [creditAmount, setCreditAmount] = useState("");
  const [bundles, setBundles] = useState([]);
  const [loadingBundles, setLoadingBundles] = useState(true);
  const [selectedBundle, setSelectedBundle] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Helper to format numbers with 'k' for thousands
  const formatNumber = (num) => {
    if (num === null || num === undefined || isNaN(num)) return num;
    const n = Number(num);
    if (n >= 1e8) return (n / 1e7).toFixed(1).replace(/\.0$/, "") + "Cr";
    if (n >= 1e5) return (n / 1e5).toFixed(1).replace(/\.0$/, "") + "L";
    if (n >= 1e4) return (n / 1e3).toFixed(1).replace(/\.0$/, "") + "k";
    return n.toLocaleString();
  };

  const formatCurrency = (num) => {
    if (num === null || num === undefined || isNaN(num)) return num;
    return Number(num).toLocaleString("en-IN", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
  };

  // Only allow up to 9 digits in creditAmount
  const handleCreditAmountChange = (e) => {
    let value = e.target.value.replace(/[^0-9]/g, "");
    if (value.length > 9) value = value.slice(0, 9);
    setCreditAmount(value);
  };
  const [walletInfo, setWalletInfo] = useState(null);
  const creditCost = walletInfo?.subscription_plan?.credit_cost || 10;
  const gstRate = walletInfo?.subscription_plan?.gst_rate || 18.0;
  const baseAmount = creditAmount ? Number(creditAmount) * creditCost : 0;
  const gstAmount = (baseAmount * gstRate) / 100;
  const totalInclGst = baseAmount + gstAmount;


  const brandId = query.get("brand_id") || onboarding?.brandId;

  useEffect(() => {
    if (!brandId) {
      navigate("/pricing", { replace: true });
      return;
    }

    const fetchData = async () => {
      setLoadingBundles(true);
      try {
        // Fetch wallet data
        const balance = await walletService.checkWalletBalance(token, brandId);
        console.log("Wallet balance:", balance);
        setWalletInfo(balance);

        // Fetch active credit bundles
        const bundleResponse = await walletService.getActiveBundles(token);
        setBundles(bundleResponse.bundles || []);
      } catch (error) {
        console.error("Error fetching wallet data or bundles:", error);
      } finally {
        setLoadingBundles(false);
      }
    };

    fetchData();
  }, [token, brandId, navigate]);

  const handleAddCredits = async (e) => {
    e.preventDefault();
    if (!creditAmount || isNaN(creditAmount) || Number(creditAmount) <= 0) {
      alert("Please enter a valid amount to add.");
      return;
    }

    try {
      const wallet_recharge_response = await walletService.walletRecharge(brandId, creditAmount, token);
      console.log("Wallet recharge response:", wallet_recharge_response);
      alert(`You will now be redirected to PayU to pay ₹${totalInclGst.toFixed(2)} (Incl. GST).`);
      walletService.submitToPayU(wallet_recharge_response.payment_data, wallet_recharge_response.payment_url);
    } catch (error) {
      console.error("Error adding credits:", error);
      alert("Failed to add credits.");
    }
  };

  const handleBundlePurchase = (bundle) => {
    setSelectedBundle(bundle);
    setShowConfirmation(true);
  };

  const confirmBundlePurchase = async () => {
    if (!selectedBundle) return;

    try {
      const response = await walletService.purchaseBundle(brandId, selectedBundle.id, token);
      console.log("Bundle purchase response:", response);
      alert(`You will now be redirected to PayU to pay ₹${selectedBundle.total_price_incl_gst} (Incl. GST).`);
      walletService.submitToPayU(response.payment_data, response.payment_url);
      setShowConfirmation(false);
      setSelectedBundle(null);
    } catch (error) {
      console.error("Error purchasing bundle:", error);
      alert(error.message || "Failed to purchase bundle.");
      setShowConfirmation(false);
      setSelectedBundle(null);
    }
  };

  const cancelBundlePurchase = () => {
    setShowConfirmation(false);
    setSelectedBundle(null);
  };

  if (!brandId || !walletInfo || !walletInfo.subscription_plan) return null;

  // Format date from ISO string to readable format
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Get status styling
  const getStatusStyles = (status) => {
    switch (status.toLowerCase()) {
      case 'success':
        return {
          background: 'rgba(16, 185, 129, 0.1)',
          color: '#10b981'
        };
      case 'failed':
        return {
          background: 'rgba(239, 68, 68, 0.1)',
          color: '#ef4444'
        };
      case 'pending':
        return {
          background: 'rgba(234, 179, 8, 0.1)',
          color: '#eab308'
        };
      default:
        return {
          background: 'rgba(107, 114, 128, 0.1)',
          color: '#6b7280'
        };
    }
  };

  return (
    <div
      style={{
        maxWidth: "1200px",
        margin: "0 auto",
        padding: "2rem",
        minHeight: "100vh",
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          pointerEvents: "none",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            background: "rgba(255, 255, 255, 0.1)",
            borderRadius: "50%",
            width: "80px",
            height: "80px",
            top: "20%",
            left: "10%",
            animation: "float 6s ease-in-out infinite -2s",
          }}
        ></div>
        <div
          style={{
            position: "absolute",
            background: "rgba(255, 255, 255, 0.1)",
            borderRadius: "50%",
            width: "60px",
            height: "60px",
            top: "60%",
            right: "15%",
            animation: "float 6s ease-in-out infinite -4s",
          }}
        ></div>
        <div
          style={{
            position: "absolute",
            background: "rgba(255, 255, 255, 0.1)",
            borderRadius: "50%",
            width: "100px",
            height: "100px",
            bottom: "20%",
            left: "20%",
            animation: "float 6s ease-in-out infinite -1s",
          }}
        ></div>
      </div>

      <div
        style={{
          textAlign: "center",
          marginBottom: "3rem",
          color: "white",
        }}
      >
        <h1
          style={{
            fontSize: "3rem",
            margin: "0 0 0.5rem 0",
            fontWeight: 700,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "1rem",
          }}
        >
          <div style={{ width: "50px", height: "50px", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <img
              src="/images/Wallet icon.svg"
              alt="wallet"
              style={{ width: "250px", height: "250px", maxWidth: "none" }}
            />
          </div>
          Wallet Dashboard
        </h1>
        <div
          style={{
            fontSize: "1.2rem",
            opacity: 0.9,
            fontWeight: 300,
            letterSpacing: "0.5px",
            color: "black",
          }}
        >
          {walletInfo.brand.name}
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "2rem",
          marginBottom: "3rem",
        }}
      >
        <div
          style={{
            background: "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(20px)",
            borderRadius: "20px",
            padding: "2.5rem",
            textAlign: "center",
            boxShadow: "0 20px 40px rgba(0, 0, 0, 0.1)",
            border: "1px solid rgba(255, 255, 255, 0.2)",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "4px",
              background: "linear-gradient(90deg, #4facfe 0%, #00f2fe 100%)",
            }}
          ></div>
          <div
            style={{
              width: "80px",
              height: "80px",
              margin: "0 auto 1.5rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
              position: "relative"
            }}
          >
            <img
              src="/images/Wallet icon.svg"
              alt="wallet"
              style={{ 
                width: "320px", 
                height: "320px", 
                maxWidth: "none",
                position: "absolute",
                top: "46%", // Moving it slightly down to center it visually
                left: "50%",
                transform: "translate(-50%, -50%)"
              }}
            />
          </div>
          <div
            style={{
              color: "#6b7280",
              fontSize: "1rem",
              marginBottom: "0.5rem",
              fontWeight: 500,
              textTransform: "uppercase",
              letterSpacing: "1px",
            }}
          >
            Current Balance
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0.5rem auto 0",
              width: "100%",
            }}
          >
            <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
              <div style={{ position: "absolute", right: "100%", marginRight: "0.75rem", display: "flex", alignItems: "center" }}>
                <img
                  src="/images/credit icon.svg"
                  alt="credit"
                  style={{ 
                    width: "36px", 
                    height: "36px", 
                    transform: "translateY(6px)",
                    flexShrink: 0
                  }}
                />
              </div>
              <h2
                style={{
                  color: "#1f2937",
                  fontSize: "3.5rem",
                  fontWeight: 700,
                  margin: 0,
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                  lineHeight: 1,
                }}
              >
              {Number.isInteger(Number(walletInfo.wallet_balance))
                ? Number(walletInfo.wallet_balance).toString()
                : Number(walletInfo.wallet_balance).toFixed(2)}
            </h2>
          </div>
        </div>
      </div>

        <div
          style={{
            background: "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(20px)",
            borderRadius: "20px",
            padding: "2.5rem",
            boxShadow: "0 20px 40px rgba(0, 0, 0, 0.1)",
            border: "1px solid rgba(255, 255, 255, 0.2)",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "4px",
              background: "linear-gradient(90deg, #ffecd2 0%, #fcb69f 100%)",
            }}
          ></div>
          <h3
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.75rem",
              color: "#1f2937",
              fontSize: "1.5rem",
              marginBottom: "1.5rem",
              fontWeight: 600,
            }}
          >
            <CreditCardIcon />
            Add Credits
          </h3>
          <div
            style={{
              position: "relative",
              marginBottom: "1.5rem",
            }}
          >
            <input
              style={{
                width: "100%",
                padding: "1rem 1.25rem",
                fontSize: "1.1rem",
                border: "2px solid #e5e7eb",
                borderRadius: "12px",
                outline: "none",
                transition: "all 0.3s ease",
                background: "rgba(255, 255, 255, 0.8)",
              }}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              placeholder="Enter credits to add"
              value={creditAmount}
              onChange={handleCreditAmountChange}
              min="0"
              maxLength={9}
            />
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "1.5rem",
              alignItems: "center",
              marginBottom: "1.5rem",
            }}
          >
            {/* Total Cost Section */}
            <div
              style={{
                background: "linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)",
                padding: "1rem 1.5rem",
                borderRadius: "12px",
                textAlign: "center",
                flex: "1",
                maxWidth: "250px",
                minHeight: "120px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              <div
                style={{
                  fontSize: "0.95rem",
                  color: "#8b5a3c",
                  marginBottom: "0.5rem",
                  fontWeight: 600,
                }}
              >
                Total Cost (Incl. GST)
              </div>
              <div
                style={{
                  fontSize: "1.8rem",
                  fontWeight: 800,
                  color: "#8b5a3c",
                }}
              >
                ₹{formatCurrency(totalInclGst)}
              </div>
            </div>

            {/* Calculation Breakdown Section */}
            <div
              style={{
                background: "linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)",
                padding: "1rem 1.5rem",
                borderRadius: "12px",
                textAlign: "center",
                flex: "1",
                maxWidth: "250px",
                minHeight: "120px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              <div
                style={{
                  fontSize: "0.9rem",
                  color: "#8b5a3c",
                  marginBottom: "0.5rem",
                  fontWeight: 600,
                  borderBottom: "1px solid rgba(139, 90, 60, 0.2)",
                  paddingBottom: "0.25rem",
                }}
              >
                Calculation
              </div>
              <div
                style={{
                  fontSize: "0.95rem",
                  fontWeight: 600,
                  color: "#8b5a3c",
                  marginBottom: "0.5rem",
                }}
              >
                {formatNumber(creditAmount || 0)} units × ₹{formatCurrency(creditCost)}/unit
              </div>
              <div
                style={{
                  fontSize: "0.95rem",
                  fontWeight: 600,
                  color: "#8b5a3c",
                  marginTop: "auto",
                  paddingTop: "0.25rem",
                  borderTop: "1px solid rgba(139, 90, 60, 0.2)",
                }}
              >
                Incl. GST ({gstRate}%): ₹{formatCurrency(gstAmount)}
              </div>
            </div>
          </div>
          <button
            style={{
              width: "100%",
              padding: "1rem",
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "white",
              border: "none",
              borderRadius: "12px",
              fontSize: "1.1rem",
              fontWeight: 600,
              cursor: "pointer",
              transition: "all 0.3s ease",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "0.5rem",
            }}
            onMouseOver={(e) => (e.currentTarget.style.transform = "translateY(-2px)")}
            onMouseOut={(e) => (e.currentTarget.style.transform = "translateY(0)")}
            onClick={handleAddCredits}
          >
            <PlusIcon />
            Add Credits
          </button>
        </div>
      </div>

      {/* Credit Bundle Section */}
      <div
        style={{
          background: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(20px)",
          borderRadius: "20px",
          padding: "2.5rem",
          boxShadow: "0 20px 40px rgba(0, 0, 0, 0.1)",
          border: "1px solid rgba(255, 255, 255, 0.2)",
          position: "relative",
          overflow: "hidden",
          marginBottom: "3rem",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "4px",
            background: "linear-gradient(90deg, #4facfe 0%, #00f2fe 100%)",
          }}
        ></div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "1rem",
            marginBottom: "2rem",
          }}
        >
          <h3
            style={{
              color: "#1f2937",
              fontSize: "1.8rem",
              margin: 0,
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              gap: "0.75rem",
              lineHeight: 1,
            }}
          >
            <img
              src="/images/credit icon.svg"
              alt="credit"
              style={{ width: "32px", height: "32px", transform: "translateY(3px)" }}
            />
            Credit Bundles
          </h3>
        </div>

        {loadingBundles ? (
          <div style={{ textAlign: "center", padding: "2rem" }}>
            <div>Loading bundles...</div>
          </div>
        ) : bundles.length === 0 ? (
          <div style={{ textAlign: "center", padding: "2rem" }}>
            <div>No credit bundles available at the moment.</div>
          </div>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
              gap: "1.5rem",
            }}
          >
            {bundles.map((bundle) => (
              <div
                key={bundle.id}
                style={{
                  background: "white",
                  borderRadius: "16px",
                  padding: "1.5rem",
                  boxShadow: "0 8px 30px rgba(0, 0, 0, 0.12)",
                  border: "1px solid #e5e7eb",
                  transition: "all 0.3s ease",
                  position: "relative",
                  overflow: "hidden",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-5px)";
                  e.currentTarget.style.boxShadow = "0 20px 40px rgba(0, 0, 0, 0.15)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 8px 30px rgba(0, 0, 0, 0.12)";
                }}
              >
                <div
                  style={{
                    textAlign: "center",
                  }}
                >
                  <h4
                    style={{
                      color: "#1f2937",
                      fontSize: "1.4rem",
                      margin: "0 0 0.5rem 0",
                      fontWeight: 600,
                    }}
                  >
                    {bundle.name}
                  </h4>
                  <div
                    style={{
                      fontSize: "2rem",
                      fontWeight: "bold",
                      color: "#667eea",
                      margin: "0.5rem 0",
                      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text",
                    }}
                  >
                    {bundle.credits} Credits
                  </div>

                   <div
                    style={{
                      fontSize: "1.6rem", // Even more prominent
                      color: "#1f2937",
                      fontWeight: 800,
                      margin: "10px 0",
                      fontFamily: "'Inter', 'Segoe UI', sans-serif",
                      letterSpacing: "-0.02em", // Modern tight feel
                      textAlign: "center"
                    }}
                  >
                    ₹{Number(bundle.price).toLocaleString()}
                  </div>
                  <div
                    style={{
                      fontSize: "0.9rem",
                      color: "#6b7280",
                      marginBottom: "0.75rem",
                    }}
                  >
                    ₹{bundle.price_per_credit} per credit
                  </div>
                  <div
                    style={{
                      fontSize: "0.9rem",
                      color: "#4b5563",
                      marginBottom: "1rem",
                      minHeight: "40px",
                    }}
                  >
                    {bundle.description}
                  </div>
                  <button
                    onClick={() => handleBundlePurchase(bundle)}
                    style={{
                      width: "100%",
                      padding: "0.75rem",
                      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      color: "white",
                      border: "none",
                      borderRadius: "8px",
                      fontSize: "1rem",
                      fontWeight: 600,
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      marginTop: "0.5rem",
                    }}
                    onMouseOver={(e) =>
                      (e.currentTarget.style.transform = "translateY(-2px)")
                    }
                    onMouseOut={(e) =>
                      (e.currentTarget.style.transform = "translateY(0)")
                    }
                  >
                    Buy Now
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div
        style={{
          background: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(20px)",
          borderRadius: "20px",
          padding: "2.5rem",
          boxShadow: "0 20px 40px rgba(0, 0, 0, 0.1)",
          border: "1px solid rgba(255, 255, 255, 0.2)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "4px",
            background: "linear-gradient(90deg, #a8edea 0%, #fed6e3 100%)",
          }}
        ></div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "1rem",
            marginBottom: "2rem",
          }}
        >
          <h3
            style={{
              color: "#1f2937",
              fontSize: "1.8rem",
              margin: 0,
              fontWeight: 600,
            }}
          >
            Transaction History
          </h3>
        </div>
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            background: 'transparent',
          }}
        >
          <thead>
            <tr>
              <th
                style={{
                  padding: '1.25rem 1rem',
                  textAlign: 'left',
                  borderBottom: '2px solid #f3f4f6',
                  color: '#6b7280',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                Date
              </th>
              <th
                style={{
                  padding: '1.25rem 1rem',
                  textAlign: 'left',
                  borderBottom: '2px solid #f3f4f6',
                  color: '#6b7280',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                Description
              </th>
              <th
                style={{
                  padding: '1.25rem 1rem',
                  textAlign: 'left',
                  borderBottom: '2px solid #f3f4f6',
                  color: '#6b7280',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                Credits
              </th>
              <th
                style={{
                  padding: '1.25rem 1rem',
                  textAlign: 'left',
                  borderBottom: '2px solid #f3f4f6',
                  color: '#6b7280',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                Status
              </th>
            </tr>
          </thead>
          <tbody>
            {walletInfo.recent_transactions.map((transaction) => (
              <tr
                key={transaction.id}
                style={{
                  borderBottom: '1px solid #f3f4f6',
                  transition: 'all 0.2s ease',
                }}
                onMouseOver={(e) =>
                  (e.currentTarget.style.background = 'rgba(102, 126, 234, 0.05)')
                }
                onMouseOut={(e) =>
                  (e.currentTarget.style.background = 'transparent')
                }
              >
                <td
                  style={{
                    padding: '1.5rem 1rem',
                    verticalAlign: 'middle',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      color: '#6b7280',
                      fontSize: '0.95rem',
                    }}
                  >
                    <CalendarIcon style={{ width: '1.25rem', height: '1.25rem' }} />
                    {formatDate(transaction.created_at)}
                  </div>
                </td>
                <td
                  style={{
                    padding: '1.5rem 1rem',
                    verticalAlign: 'middle',
                    color: '#1f2937',
                    fontWeight: 500,
                  }}
                >
                  {transaction.description}
                </td>
                <td
                  style={{
                    padding: '1.5rem 1rem',
                    verticalAlign: 'middle',
                  }}
                >
                  <div
                    style={{
                      fontWeight: 700,
                      fontSize: '1.1rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      color: transaction.transaction_type === 'credit' ? '#10b981' : '#ef4444',
                    }}
                  >
                    {transaction.transaction_type === 'credit' ? (
                      <TrendingUpIcon style={{ width: '1.25rem', height: '1.25rem' }} />
                    ) : (
                      <TrendingDownIcon style={{ width: '1.25rem', height: '1.25rem' }} />
                    )}
                    {transaction.transaction_type === 'credit' ? '+' : '-'}
                    {parseFloat(transaction.amount).toFixed(2)}
                  </div>
                </td>
                <td
                  style={{
                    padding: '1.5rem 1rem',
                    verticalAlign: 'middle',
                  }}
                >
                  <div
                    style={{
                      ...getStatusStyles(transaction.status),
                      padding: '0.5rem 1rem',
                      borderRadius: '20px',
                      fontSize: '0.85rem',
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                    }}
                  >
                    <CheckCircleIcon style={{ width: '1.25rem', height: '1.25rem' }} />
                    {transaction.status.charAt(0).toUpperCase() + transaction.status.slice(1)}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <style>
        {`
          @keyframes float {
            0%, 100% {
              transform: translateY(0) rotate(0deg);
              opacity: 0.5;
            }
            50% {
              transform: translateY(-20px) rotate(180deg);
              opacity: 0.8;
            }
          }

          @media (max-width: 768px) {
            div[style*="max-width: 1200px"] {
              padding: 1rem;
            }

            div[style*="grid-template-columns: 1fr 1fr"] {
              grid-template-columns: 1fr;
              gap: 1.5rem;
            }

            h1[style*="font-size: 3rem"] {
              font-size: 2rem;
            }

            h2[style*="font-size: 3.5rem"] {
              font-size: 2.5rem;
            }

            table[style*="width: 100%"] {
              font-size: 0.9rem;
            }

            td[style*="padding: 1.5rem 1rem"] {
              padding: 1rem 0.5rem;
            }

            div[style*="padding: 2.5rem"][style*="background: rgba(255, 255, 255, 0.95)"][style*="border-radius: 20px"] {
              padding: 1.5rem;
            }
          }
        `}
      </style>

      {/* Bundle Purchase Confirmation Modal */}
      {showConfirmation && selectedBundle && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: "12px",
              padding: "2rem",
              maxWidth: "500px",
              width: "90%",
              boxShadow: "0 20px 40px rgba(0, 0, 0, 0.2)",
            }}
          >
            <h3
              style={{
                color: "#1f2937",
                fontSize: "1.5rem",
                margin: "0 0 1rem 0",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
              }}
            >
              <GiftIcon />
              Confirm Purchase
            </h3>
            <div style={{ marginBottom: "1.5rem" }}>
              <p style={{ margin: "0.5rem 0", color: "#4b5563" }}>
                <strong>Bundle:</strong> {selectedBundle.name}
              </p>
              <p style={{ margin: "0.5rem 0", color: "#4b5563" }}>
                <strong>Credits:</strong> {selectedBundle.credits}
              </p>
              <p style={{ margin: "0.5rem 0", color: "#4b5563" }}>
                <strong>Base Price:</strong> ₹{selectedBundle.price}
              </p>
              <p style={{ margin: "0.5rem 0", color: "#6b7280", fontSize: "0.9rem" }}>
                + GST ({selectedBundle.gst_rate}%): ₹{selectedBundle.gst_amount}
              </p>
              <p style={{ margin: "0.8rem 0", color: "#10b981", fontSize: "1.2rem" }}>
                <strong>Total Price (Incl. GST):</strong> ₹{selectedBundle.total_price_incl_gst}
              </p>
            </div>
            <div style={{ display: "flex", gap: "1rem", justifyContent: "flex-end" }}>
              <button
                onClick={cancelBundlePurchase}
                style={{
                  padding: "0.75rem 1.5rem",
                  background: "#e5e7eb",
                  color: "#4b5563",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "1rem",
                  fontWeight: 600,
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                }}
                onMouseOver={(e) =>
                  (e.currentTarget.style.background = "#d1d5db")
                }
                onMouseOut={(e) =>
                  (e.currentTarget.style.background = "#e5e7eb")
                }
              >
                Cancel
              </button>
              <button
                onClick={confirmBundlePurchase}
                style={{
                  padding: "0.75rem 1.5rem",
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "1rem",
                  fontWeight: 600,
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                }}
                onMouseOver={(e) =>
                  (e.currentTarget.style.transform = "translateY(-2px)")
                }
                onMouseOut={(e) =>
                  (e.currentTarget.style.transform = "translateY(0)")
                }
              >
                Confirm Purchase
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WalletPage;
